import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
} from "@react-google-maps/api";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useHeroAPI from "../../api/useHeroAPI";
import usePreDefinedListAPI from "../../api/usePreDefinedListAPI";
import { setLoading } from "../../redux/loadingSlice";
import { selectCityData } from "../../redux/citySlice";

import "./StoreLocator.css";
import AppSkeleton from "../AppSkeleton/AppSkeleton";

const libraries: "places"[] = ["places"];

// NOTE: adjust these two keys if your service point API response uses
// different field names for the parent state / city ids.
const SP_STATE_ID_KEY = "state_id";
const SP_CITY_ID_KEY = "city_id";

// ========== DISTANCE CALCULATION HELPER ==========
// Calculate distance between two coordinates using Haversine formula
// Returns distance in kilometers
const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const StoreLocator = () => {
  // Fallback center used until we know the user's location / selection
  const defaultCenter = { lat: 19.186021, lng: 72.856318 }; // Mumbai

  const dispatch = useDispatch();
  const { getSPList } = usePreDefinedListAPI();
  const { predefinedFunc } = useHeroAPI();

  // Get selected city from header (Redux)
  const headerSelectedCity = useSelector(selectCityData);

  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [selectedState, setSelectedState] = useState<any>(null);

  const [cityOptions, setCityOptions] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState<any>(null);

  const [selected, setSelected] = useState<any>(null);
  const [searchInput, setSearchInput] = useState("");
  const [allServicePoints, setAllServicePoints] = useState<any[]>([]);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [locationChecked, setLocationChecked] = useState(false);
  const [fetchingServicePoints, setFetchingServicePoints] = useState(false);

  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(
    window.innerWidth >= 760,
  );
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(5);

  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const [locatingMe, setLocatingMe] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  const customMarkerIcon = {
    url: "https://mechmiles.com/location-pin.png",
    scaledSize: { width: 50, height: 50 } as google.maps.Size,
  };

  const currentLocationIcon = {
    path: window.google?.maps?.SymbolPath?.CIRCLE,
    scale: 8,
    fillColor: "#2563eb",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  };

  useEffect(() => {
    const hasReloaded = sessionStorage.getItem("storeLocatorReloaded");

    if (!hasReloaded) {
      sessionStorage.setItem("storeLocatorReloaded", "true");
      window.location.reload();
    }

    return () => {
      sessionStorage.removeItem("storeLocatorReloaded");
    };
  }, []);

  // ---------- API helpers ----------

  const fetchStateOptions = async () => {
    try {
      const response = await predefinedFunc({ entity_type: "STATE" });
      const states =
        response?.data?.data?.map((child: any) => ({
          name: child.name,
          code: child.predefined_id,
          lat: child.lat,
          lng: child.lng,
        })) || [];
      setStateOptions(states);
      return states;
    } catch (err) {
      console.error("Failed to fetch states", err);
      return [];
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchCityOptions = async (state: any) => {
    try {
      const response = await predefinedFunc({
        entity_type: "CITY",
        parent_id: state.code,
      });
      const cities = response?.data?.data || [];
      setCityOptions(cities);
      return cities;
    } catch (err) {
      console.error("Failed to fetch cities", err);
      return [];
    }
  };

  // Fetch every service point once; all state/city/search narrowing happens
  // client-side afterwards (see `filteredServicePoints` below).
  const fetchAllServicePoints = async () => {
    try {
      setFetchingServicePoints(true);
      dispatch(setLoading(true));
      const response: any = await getSPList({flag:"website"});
      if (response.status === 200) {
        const mapped = (response?.data || [])
          .map((sp: any) => ({
            id: sp?.service_point_id,
            name: sp?.service_point_name,
            location:
              sp?.lat && sp?.lng
                ? { lat: parseFloat(sp.lat), lng: parseFloat(sp.lng) }
                : null,
            subTitle: sp?.description,
            address: sp?.service_point_address,
            phone: sp?.service_point_contact,
            stateId: sp?.[SP_STATE_ID_KEY],
            cityId: sp?.[SP_CITY_ID_KEY],
          }))
          .sort((a: any, b: any) => {
            if (a.location && !b.location) return -1;
            if (!a.location && b.location) return 1;
            return 0;
          });
        setAllServicePoints(mapped);
      }
    } finally {
      dispatch(setLoading(false));
      setFetchingServicePoints(false);
    }
  };

  // ---------- Geolocation-driven auto selection ----------

  const detectUserLocation = (states: any[]) => {
    if (!navigator.geolocation) {
      setLocationChecked(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const geocoder = new window.google.maps.Geocoder();
        setCurrentLocation({ lat: latitude, lng: longitude });
        //         // setMapCenter({ lat: latitude, lng: longitude });
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          async (results: any, status: any) => {
            if (status === "OK" && results?.length) {
              const addressComponents = results[0].address_components;

              const stateComponent = addressComponents.find((c: any) =>
                c.types.includes("administrative_area_level_1"),
              );
              const cityComponent = addressComponents.find((c: any) =>
                c.types.includes("locality"),
              );

              const matchedState = stateComponent
                ? states.find(
                    (s: any) =>
                      s.name.trim().toLowerCase() ===
                      stateComponent.long_name.trim().toLowerCase(),
                  )
                : null;

              if (matchedState) {
                setSelectedState(matchedState);
                const cities = await fetchCityOptions(matchedState);

                const matchedCity = cityComponent
                  ? cities.find(
                      (c: any) =>
                        c.name.trim().toLowerCase() ===
                        cityComponent.long_name.trim().toLowerCase(),
                    )
                  : null;

                if (matchedCity) {
                  setSelectedCity(matchedCity);
                  if (matchedCity?.lat && matchedCity?.lng) {
                    setMapCenter({
                      lat: parseFloat(matchedCity.lat),
                      lng: parseFloat(matchedCity.lng),
                    });
                    setMapZoom(11);
                  }
                }
              }
            }
            setLocationChecked(true);
          },
        );
      },
      () => setLocationChecked(true),
    );
  };

  // ---------- Effects ----------

  // Step 1: fetch state list + the full service point list in parallel on mount
  useEffect(() => {
    dispatch(setLoading(true));
    fetchStateOptions();
    fetchAllServicePoints();
  }, []);

  // Step 2: once map + states are ready, try to auto-detect state/city from geolocation
  useEffect(() => {
    if (mapLoaded && stateOptions.length > 0 && !locationChecked) {
      detectUserLocation(stateOptions);
    }
  }, [mapLoaded, stateOptions, locationChecked]);

  useEffect(() => {
    const handleResize = () => setIsSearchPanelOpen(window.innerWidth >= 760);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sync with header city selection (Redux)
  useEffect(() => {
    // console.log("useSelector(selectCity)",headerSelectedCity,stateOptions,selectedCity);
    if (
      headerSelectedCity?.predefined_id &&
      stateOptions.length > 0 &&
      allServicePoints.length > 0
    ) {
      // Find state that contains this city
      const matchedState = stateOptions.find(
        (s: any) => String(s.code) === String(headerSelectedCity.parent_id),
      );

      if (matchedState) {
        setSelectedState(matchedState);
        const cities = fetchCityOptions(matchedState);

        setSelectedCity(headerSelectedCity);

        // Update map center
        if (headerSelectedCity?.lat && headerSelectedCity?.lng) {
          setMapCenter({
            lat: parseFloat(headerSelectedCity.lat),
            lng: parseFloat(headerSelectedCity.lng),
          });
          setMapZoom(11);
        }
      }
    }
  }, [headerSelectedCity, stateOptions, allServicePoints]);

  // Recenter the map whenever the state/city selection narrows, so results
  // stay visible even if geolocation didn't drive the change.
  useEffect(() => {
    if (selectedCity?.lat && selectedCity?.lng) {
      setMapCenter({
        lat: parseFloat(selectedCity.lat),
        lng: parseFloat(selectedCity.lng),
      });
      setMapZoom(11);
    } else if (selectedState?.lat && selectedState?.lng) {
      setMapCenter({
        lat: parseFloat(selectedState.lat),
        lng: parseFloat(selectedState.lng),
      });
      setMapZoom(7);
    } else if (!selectedState && !selectedCity) {
      setMapCenter(defaultCenter);
      setMapZoom(5);
    }
  }, [selectedState, selectedCity]);

  // ---------- Client-side filtering with distance sorting ----------
  // Determine reference point for distance calculation
  const getReferencePoint = (): { lat: number; lng: number } | null => {
    // Priority: current location > selected city > selected state > default
    if (currentLocation) {
      return currentLocation;
    }
    if (selectedCity?.lat && selectedCity?.lng) {
      return {
        lat: parseFloat(selectedCity.lat),
        lng: parseFloat(selectedCity.lng),
      };
    }
    if (selectedState?.lat && selectedState?.lng) {
      return {
        lat: parseFloat(selectedState.lat),
        lng: parseFloat(selectedState.lng),
      };
    }
    return null;
  };

  const filteredServicePoints = useMemo(() => {
    let points = allServicePoints;

    if (selectedCity) {
      points = points.filter(
        (p: any) => String(p.cityId) === String(selectedCity.predefined_id),
      );
    } else if (selectedState) {
      points = points.filter(
        (p: any) => String(p.stateId) === String(selectedState.code),
      );
    }

    if (searchInput.trim()) {
      const term = searchInput.trim().toLowerCase();
      points = points.filter(
        (p: any) =>
          p.name?.toLowerCase().includes(term) ||
          p.address?.toLowerCase().includes(term),
      );
    }

    // ========== DISTANCE SORTING LOGIC ==========
    const referencePoint = getReferencePoint();
    if (referencePoint) {
      points = points.map((point: any) => ({
        ...point,
        distance: point.location
          ? calculateDistance(
              referencePoint.lat,
              referencePoint.lng,
              point.location.lat,
              point.location.lng,
            )
          : Infinity,
      }));

      // Sort by distance (nearest first, points without location go to end)
      points.sort((a: any, b: any) => a.distance - b.distance);
    }

    return points;
  }, [
    allServicePoints,
    selectedState,
    selectedCity,
    searchInput,
    currentLocation,
  ]);

  // ---------- Handlers ----------

  const handleStateChange = async (state: any) => {
    if (!state) {
      setSelectedState(null);
      setSelectedCity(null);
      setCityOptions([]);
      return;
    }
    setSelectedState(state);
    setSelectedCity(null);
    await fetchCityOptions(state);
  };

  const handleCityChange = (e: any) => {
    const city = e.value;
    if (!city) {
      setSelectedCity(null);
      setSelected(null);
      return;
    }
    setSelectedCity(city);
  };

  const handleClearFilters = () => {
    setSelectedState(null);
    setSelectedCity(null);
    setCityOptions([]);
    setSearchInput("");
    setSelected(null);
  };

  const handleSearchInput = (e: any) => setSearchInput(e.target.value);

  const handleServicePointSelection = (servicePoint: any) => {
    setSelected(servicePoint);
    if (servicePoint.location) {
      setMapCenter(servicePoint.location);
      setMapZoom(15);
    }
    if (window.innerWidth < 760) setIsSearchPanelOpen(false);
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      setLocateError("Geolocation is not supported by this browser.");
      return;
    }
    setLocatingMe(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
        setMapCenter({ lat: latitude, lng: longitude });
        setMapZoom(15);
        setLocatingMe(false);
      },
      () => {
        setLocateError("Unable to retrieve your location.");
        setLocatingMe(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const getDirectionFunc = (lat: any, lng: any) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format distance for display
  const formatDistance = (distance: number): string => {
    if (distance === Infinity) return "N/A";
    return distance < 1
      ? `${(distance * 1000).toFixed(0)}m`
      : `${distance.toFixed(1)}km`;
  };

  const hasActiveFilters = Boolean(
    selectedState || selectedCity || searchInput,
  );

  return (
    <div className="map-wrapper">
      {fetchingServicePoints && (
        // <div className="loading-overlay">Fetching Service Points Data...</div>
        <AppSkeleton
          count={1}
          width="100p%"
          height="100%"
          borderRadius="12px"
          containerClassName="popular-brands-skeleton-grid"
          className="popular-brands-skeleton-item"
        />
      )}

      <LoadScript
        googleMapsApiKey="AIzaSyBc8f4IQtJeK5lr0iEhUy0KbFQCe4JCPnQ"
        libraries={libraries}
        onLoad={() => setMapLoaded(true)}
      >
        <div className="container-fluid p-0">
          <div className="map-container">
            {!isSearchPanelOpen && window.innerWidth < 760 && (
              <div
                className="reopen-search-panel"
                onClick={() => setIsSearchPanelOpen(true)}
              >
                🔍
              </div>
            )}

            {isSearchPanelOpen && mapLoaded && (
              <div className="location-search-panel11">
                <div className="sp-panel-header">
                  <span className="sp-panel-title">Find a Service Point</span>
                  {window.innerWidth < 760 && (
                    <button
                      type="button"
                      className="sp-panel-close"
                      onClick={() => setIsSearchPanelOpen(false)}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="sp-filters">
                  <div className="sp-row-two">
                    <div className="sp-field sp-field-outline">
                      <span className="sp-field-legend">State</span>
                      <Dropdown
                        inputId="state"
                        className="sp-plain-dropdown "
                        value={selectedState}
                        options={stateOptions}
                        onChange={(e) => handleStateChange(e.value)}
                        placeholder="All States"
                        optionLabel="name"
                        filter
                        scrollHeight="160px"
                        virtualScrollerOptions={{ itemSize: 40 }}
                      />
                    </div>

                    <div className="sp-field sp-field-outline">
                      <span className="sp-field-legend">City</span>
                      <Dropdown
                        inputId="city"
                        className="sp-plain-dropdown"
                        value={selectedCity}
                        options={cityOptions}
                        onChange={handleCityChange}
                        placeholder={
                          selectedState ? "All Cities" : "Select a state"
                        }
                        optionLabel="name"
                        filter
                        disabled={!selectedState}
                        scrollHeight="160px"
                        virtualScrollerOptions={{ itemSize: 40 }}
                      />
                    </div>
                  </div>

                  <div className="sp-field sp-field-outline">
                    <span className="sp-field-legend">Search</span>
                    <InputText
                      id="search"
                      placeholder="Search by name or address"
                      className="sp-plain-input w-full"
                      value={searchInput}
                      onChange={handleSearchInput}
                    />
                  </div>

                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="sp-clear-filters"
                      onClick={handleClearFilters}
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
                <div className="sp-results-count">
                  {filteredServicePoints.length} service point
                  {filteredServicePoints.length === 1 ? "" : "s"} found
                </div>

                <div className="sp-results-list">
                  {!filteredServicePoints?.length && (
                    <div className="empty-service-point-list">
                      No Service Points Found
                    </div>
                  )}

                  {filteredServicePoints?.map((point: any) => (
                    <div
                      key={point.id}
                      className={
                        "location-sp-option-wrapper" +
                        (selected?.id === point.id ? " is-selected" : "")
                      }
                    >
                      <div className="location-sp-name-wrapper">
                        <div className="location-sp-name">{point.name}</div>
                        <div className="location-sp-address">
                          {point.address}
                        </div>
                        {/* Display distance if reference point exists */}
                        {getReferencePoint() && point.distance !== Infinity && (
                          <div className="location-sp-distance">
                            📍 {formatDistance(point.distance)} away
                          </div>
                        )}
                      </div>
                      <div className="location-sp-locate-btn">
                        <Button
                          type="button"
                          disabled={!point.location}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleServicePointSelection(point);
                          }}
                        >
                          Select
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              className="locate-me-btn"
              onClick={handleLocateMe}
              disabled={locatingMe}
              title="Show my current location"
              aria-label="Show my current location"
            >
              {locatingMe ? (
                <span className="locate-me-spinner" />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="3" fill="#2563eb" />
                  <path
                    d="M12 2V5M12 19V22M2 12H5M19 12H22"
                    stroke="#2563eb"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="7"
                    stroke="#2563eb"
                    strokeWidth="1.5"
                  />
                </svg>
              )}
            </button>

            {locateError && (
              <div className="locate-me-error">{locateError}</div>
            )}

            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "94vmin" }}
              center={mapCenter}
              zoom={mapZoom}
            >
              {filteredServicePoints
                ?.filter((p: any) => p.location)
                ?.map((point: any) => (
                  <Marker
                    key={point.id}
                    position={point.location}
                    onClick={() => setSelected(point)}
                    icon={customMarkerIcon}
                  />
                ))}

              {currentLocation && (
                <Marker
                  position={currentLocation}
                  icon={currentLocationIcon}
                  zIndex={999}
                />
              )}

              {selected?.location && (
                <InfoWindow
                  position={selected.location}
                  onCloseClick={() => setSelected(null)}
                >
                  <div className="map-location-tooltip">
                    <div className="selected-service-point-name">
                      {selected?.name}
                    </div>
                    <div className="selected-service-point-address">
                      {selected?.address ?? "Address Not Available"}
                    </div>
                    {/* Show distance in info window */}
                    {getReferencePoint() && selected.distance !== Infinity && (
                      <div className="selected-service-point-distance">
                        Distance: {formatDistance(selected.distance)}
                      </div>
                    )}
                    <div className="selected-service-point-goto-location-wrapper">
                      <div
                        className="selected-service-point-goto-location"
                        onClick={() =>
                          getDirectionFunc(
                            selected?.location.lat,
                            selected?.location.lng,
                          )
                        }
                      >
                        Go To Location
                      </div>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>
        </div>
      </LoadScript>
    </div>
  );
};

export default StoreLocator;

// import {
//   GoogleMap,
//   InfoWindow,
//   LoadScript,
//   Marker,
// } from "@react-google-maps/api";
// import { Button } from "primereact/button";
// import { Dropdown } from "primereact/dropdown";
// import { InputText } from "primereact/inputtext";
// import { useEffect, useMemo, useState } from "react";
// import { useDispatch } from "react-redux";
// import useHeroAPI from "../../api/useHeroAPI";
// import usePreDefinedListAPI from "../../api/usePreDefinedListAPI";
// import { setLoading } from "../../redux/loadingSlice";

// import "./StoreLocator.css";
// import AppSkeleton from "../AppSkeleton/AppSkeleton";

// const libraries: "places"[] = ["places"];

// // NOTE: adjust these two keys if your service point API response uses
// // different field names for the parent state / city ids.
// const SP_STATE_ID_KEY = "state_id";
// const SP_CITY_ID_KEY = "city_id";

// // ========== DISTANCE CALCULATION HELPER ==========
// // Calculate distance between two coordinates using Haversine formula
// // Returns distance in kilometers
// const calculateDistance = (
//   lat1: number,
//   lon1: number,
//   lat2: number,
//   lon2: number
// ): number => {
//   const R = 6371; // Earth's radius in km
//   const dLat = ((lat2 - lat1) * Math.PI) / 180;
//   const dLon = ((lon2 - lon1) * Math.PI) / 180;
//   const a =
//     Math.sin(dLat / 2) * Math.sin(dLat / 2) +
//     Math.cos((lat1 * Math.PI) / 180) *
//       Math.cos((lat2 * Math.PI) / 180) *
//       Math.sin(dLon / 2) *
//       Math.sin(dLon / 2);
//   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
//   return R * c;
// };

// const StoreLocator = () => {
//   // Fallback center used until we know the user's location / selection
//   const defaultCenter = { lat: 19.186021, lng: 72.856318 }; // Mumbai

//   const dispatch = useDispatch();
//   const { getSPList } = usePreDefinedListAPI();
//   const { predefinedFunc } = useHeroAPI();

//   const [stateOptions, setStateOptions] = useState<any[]>([]);
//   const [selectedState, setSelectedState] = useState<any>(null);

//   const [cityOptions, setCityOptions] = useState<any[]>([]);
//   const [selectedCity, setSelectedCity] = useState<any>(null);

//   const [selected, setSelected] = useState<any>(null);
//   const [searchInput, setSearchInput] = useState("");
//   const [allServicePoints, setAllServicePoints] = useState<any[]>([]);

//   const [mapLoaded, setMapLoaded] = useState(false);
//   const [locationChecked, setLocationChecked] = useState(false);
//   const [fetchingServicePoints, setFetchingServicePoints] = useState(false);

//   const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(
//     window.innerWidth >= 760,
//   );
//   const [mapCenter, setMapCenter] = useState(defaultCenter);
//   const [mapZoom, setMapZoom] = useState(5);

//   const [currentLocation, setCurrentLocation] = useState<any>(null);
//   const [locatingMe, setLocatingMe] = useState(false);
//   const [locateError, setLocateError] = useState<string | null>(null);

//   const customMarkerIcon = {
//     url: "https://mechmiles.com/location-pin.png",
//     scaledSize: { width: 50, height: 50 } as google.maps.Size,
//   };

//   const currentLocationIcon = {
//     path: window.google?.maps?.SymbolPath?.CIRCLE,
//     scale: 8,
//     fillColor: "#2563eb",
//     fillOpacity: 1,
//     strokeColor: "#ffffff",
//     strokeWeight: 2,
//   };

//   useEffect(() => {
//     const hasReloaded = sessionStorage.getItem("storeLocatorReloaded");

//     if (!hasReloaded) {
//       sessionStorage.setItem("storeLocatorReloaded", "true");
//       window.location.reload();
//     }

//     return () => {
//       sessionStorage.removeItem("storeLocatorReloaded");
//     };
//   }, []);

//   // ---------- API helpers ----------

//   const fetchStateOptions = async () => {
//     try {
//       const response = await predefinedFunc({ entity_type: "STATE" });
//       const states =
//         response?.data?.data?.map((child: any) => ({
//           name: child.name,
//           code: child.predefined_id,
//           lat: child.lat,
//           lng: child.lng,
//         })) || [];
//       setStateOptions(states);
//       return states;
//     } catch (err) {
//       console.error("Failed to fetch states", err);
//       return [];
//     } finally {
//       dispatch(setLoading(false));
//     }
//   };

//   const fetchCityOptions = async (state: any) => {
//     try {
//       const response = await predefinedFunc({
//         entity_type: "CITY",
//         parent_id: state.code,
//       });
//       const cities = response?.data?.data || [];
//       setCityOptions(cities);
//       return cities;
//     } catch (err) {
//       console.error("Failed to fetch cities", err);
//       return [];
//     }
//   };

//   // Fetch every service point once; all state/city/search narrowing happens
//   // client-side afterwards (see `filteredServicePoints` below).
//   const fetchAllServicePoints = async () => {
//     try {
//       setFetchingServicePoints(true);
//       dispatch(setLoading(true));
//       const response: any = await getSPList({});
//       if (response.status === 200) {
//         const mapped = (response?.data || [])
//           .map((sp: any) => ({
//             id: sp?.service_point_id,
//             name: sp?.service_point_name,
//             location:
//               sp?.lat && sp?.lng
//                 ? { lat: parseFloat(sp.lat), lng: parseFloat(sp.lng) }
//                 : null,
//             subTitle: sp?.description,
//             address: sp?.service_point_address,
//             phone: sp?.service_point_contact,
//             stateId: sp?.[SP_STATE_ID_KEY],
//             cityId: sp?.[SP_CITY_ID_KEY],
//           }))
//           .sort((a: any, b: any) => {
//             if (a.location && !b.location) return -1;
//             if (!a.location && b.location) return 1;
//             return 0;
//           });
//         setAllServicePoints(mapped);
//       }
//     } finally {
//       dispatch(setLoading(false));
//       setFetchingServicePoints(false);
//     }
//   };

//   // ---------- Geolocation-driven auto selection ----------

//   const detectUserLocation = (states: any[]) => {
//     if (!navigator.geolocation) {
//       setLocationChecked(true);
//       return;
//     }

//     navigator.geolocation.getCurrentPosition(
//       (position) => {
//         const { latitude, longitude } = position.coords;
//         const geocoder = new window.google.maps.Geocoder();
//         setCurrentLocation({ lat: latitude, lng: longitude });
//         // setMapCenter({ lat: latitude, lng: longitude });
//         geocoder.geocode(
//           { location: { lat: latitude, lng: longitude } },
//           async (results: any, status: any) => {
//             if (status === "OK" && results?.length) {
//               const addressComponents = results[0].address_components;

//               const stateComponent = addressComponents.find((c: any) =>
//                 c.types.includes("administrative_area_level_1"),
//               );
//               const cityComponent = addressComponents.find((c: any) =>
//                 c.types.includes("locality"),
//               );

//               const matchedState = stateComponent
//                 ? states.find(
//                     (s: any) =>
//                       s.name.trim().toLowerCase() ===
//                       stateComponent.long_name.trim().toLowerCase(),
//                   )
//                 : null;

//               if (matchedState) {
//                 setSelectedState(matchedState);
//                 const cities = await fetchCityOptions(matchedState);

//                 const matchedCity = cityComponent
//                   ? cities.find(
//                       (c: any) =>
//                         c.name.trim().toLowerCase() ===
//                         cityComponent.long_name.trim().toLowerCase(),
//                     )
//                   : null;

//                 if (matchedCity) {
//                   setSelectedCity(matchedCity);
//                   if (matchedCity?.lat && matchedCity?.lng) {
//                     setMapCenter({
//                       lat: parseFloat(matchedCity.lat),
//                       lng: parseFloat(matchedCity.lng),
//                     });
//                     setMapZoom(11);
//                   }
//                 }
//               }
//             }
//             setLocationChecked(true);
//           },
//         );
//       },
//       () => setLocationChecked(true),
//     );
//   };

//   // ---------- Effects ----------

//   // Step 1: fetch state list + the full service point list in parallel on mount
//   useEffect(() => {
//     dispatch(setLoading(true));
//     fetchStateOptions();
//     fetchAllServicePoints();
//   }, []);

//   // Step 2: once map + states are ready, try to auto-detect state/city from geolocation
//   useEffect(() => {
//     if (mapLoaded && stateOptions.length > 0 && !locationChecked) {
//       detectUserLocation(stateOptions);
//     }
//   }, [mapLoaded, stateOptions, locationChecked]);

//   useEffect(() => {
//     const handleResize = () => setIsSearchPanelOpen(window.innerWidth >= 760);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // Recenter the map whenever the state/city selection narrows, so results
//   // stay visible even if geolocation didn't drive the change.
//   useEffect(() => {
//     if (selectedCity?.lat && selectedCity?.lng) {
//       setMapCenter({
//         lat: parseFloat(selectedCity.lat),
//         lng: parseFloat(selectedCity.lng),
//       });
//       setMapZoom(11);
//     } else if (selectedState?.lat && selectedState?.lng) {
//       setMapCenter({
//         lat: parseFloat(selectedState.lat),
//         lng: parseFloat(selectedState.lng),
//       });
//       setMapZoom(7);
//     } else if (!selectedState && !selectedCity) {
//       setMapCenter(defaultCenter);
//       setMapZoom(5);
//     }
//   }, [selectedState, selectedCity]);

//   // ---------- Client-side filtering with distance sorting ----------
//   // Determine reference point for distance calculation
//   const getReferencePoint = (): { lat: number; lng: number } | null => {
//     // Priority: current location > selected city > selected state > default
//     if (currentLocation) {
//       return currentLocation;
//     }
//     if (selectedCity?.lat && selectedCity?.lng) {
//       return {
//         lat: parseFloat(selectedCity.lat),
//         lng: parseFloat(selectedCity.lng),
//       };
//     }
//     if (selectedState?.lat && selectedState?.lng) {
//       return {
//         lat: parseFloat(selectedState.lat),
//         lng: parseFloat(selectedState.lng),
//       };
//     }
//     return null;
//   };

//   const filteredServicePoints = useMemo(() => {
//     let points = allServicePoints;

//     if (selectedCity) {
//       points = points.filter(
//         (p: any) => String(p.cityId) === String(selectedCity.predefined_id),
//       );
//     } else if (selectedState) {
//       points = points.filter(
//         (p: any) => String(p.stateId) === String(selectedState.code),
//       );
//     }

//     if (searchInput.trim()) {
//       const term = searchInput.trim().toLowerCase();
//       points = points.filter(
//         (p: any) =>
//           p.name?.toLowerCase().includes(term) ||
//           p.address?.toLowerCase().includes(term),
//       );
//     }

//     // ========== DISTANCE SORTING LOGIC ==========
//     const referencePoint = getReferencePoint();
//     if (referencePoint) {
//       points = points.map((point: any) => ({
//         ...point,
//         distance: point.location
//           ? calculateDistance(
//               referencePoint.lat,
//               referencePoint.lng,
//               point.location.lat,
//               point.location.lng
//             )
//           : Infinity,
//       }));

//       // Sort by distance (nearest first, points without location go to end)
//       points.sort((a: any, b: any) => a.distance - b.distance);
//     }

//     return points;
//   }, [allServicePoints, selectedState, selectedCity, searchInput, currentLocation]);

//   // ---------- Handlers ----------

//   const handleStateChange = async (state: any) => {
//     if (!state) {
//       setSelectedState(null);
//       setSelectedCity(null);
//       setCityOptions([]);
//       return;
//     }
//     setSelectedState(state);
//     setSelectedCity(null);
//     await fetchCityOptions(state);
//   };

//   const handleCityChange = (e: any) => {
//     const city = e.value;
//     if (!city) {
//       setSelectedCity(null);
//       setSelected(null);
//       return;
//     }
//     setSelectedCity(city);
//   };

//   const handleClearFilters = () => {
//     setSelectedState(null);
//     setSelectedCity(null);
//     setCityOptions([]);
//     setSearchInput("");
//     setSelected(null);
//   };

//   const handleSearchInput = (e: any) => setSearchInput(e.target.value);

//   const handleServicePointSelection = (servicePoint: any) => {
//     setSelected(servicePoint);
//     if (servicePoint.location) {
//       setMapCenter(servicePoint.location);
//       setMapZoom(15);
//     }
//     if (window.innerWidth < 760) setIsSearchPanelOpen(false);
//   };

//   const handleLocateMe = () => {
//     if (!navigator.geolocation) {
//       setLocateError("Geolocation is not supported by this browser.");
//       return;
//     }
//     setLocatingMe(true);
//     setLocateError(null);
//     navigator.geolocation.getCurrentPosition(
//       (position) => {
//         const { latitude, longitude } = position.coords;
//         setCurrentLocation({ lat: latitude, lng: longitude });
//         setMapCenter({ lat: latitude, lng: longitude });
//         setMapZoom(15);
//         setLocatingMe(false);
//       },
//       () => {
//         setLocateError("Unable to retrieve your location.");
//         setLocatingMe(false);
//       },
//       { enableHighAccuracy: true, timeout: 10000 },
//     );
//   };

//   const getDirectionFunc = (lat: any, lng: any) => {
//     const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
//     const link = document.createElement("a");
//     link.href = url;
//     link.target = "_blank";
//     link.rel = "noopener noreferrer";
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   // Format distance for display
//   const formatDistance = (distance: number): string => {
//     if (distance === Infinity) return "N/A";
//     return distance < 1
//       ? `${(distance * 1000)?.toFixed(0)}m`
//       : `${distance?.toFixed(1)}km`;
//   };

//   const hasActiveFilters = Boolean(
//     selectedState || selectedCity || searchInput,
//   );

//   return (
//     <div className="map-wrapper">
//       {fetchingServicePoints && (
//         // <div className="loading-overlay">Fetching Service Points Data...</div>
//         <AppSkeleton
//           count={1}
//           width="100p%"
//           height="100%"
//           borderRadius="12px"
//           containerClassName="popular-brands-skeleton-grid"
//           className="popular-brands-skeleton-item"
//         />
//       )}

//       <LoadScript
//         googleMapsApiKey="AIzaSyBc8f4IQtJeK5lr0iEhUy0KbFQCe4JCPnQ"
//         libraries={libraries}
//         onLoad={() => setMapLoaded(true)}
//       >
//         <div className="container-fluid p-0">
//           <div className="map-container">
//             {!isSearchPanelOpen && window.innerWidth < 760 && (
//               <div
//                 className="reopen-search-panel"
//                 onClick={() => setIsSearchPanelOpen(true)}
//               >
//                 🔍
//               </div>
//             )}

//             {isSearchPanelOpen && mapLoaded && (
//               <div className="location-search-panel11">
//                 <div className="sp-panel-header">
//                   <span className="sp-panel-title">Find a Service Point</span>
//                   {window.innerWidth < 760 && (
//                     <button
//                       type="button"
//                       className="sp-panel-close"
//                       onClick={() => setIsSearchPanelOpen(false)}
//                       aria-label="Close"
//                     >
//                       ✕
//                     </button>
//                   )}
//                 </div>

//                 <div className="sp-filters">
//                   <div className="sp-row-two">
//                     <div className="sp-field sp-field-outline">
//                       <span className="sp-field-legend">State</span>
//                       <Dropdown
//                         inputId="state"
//                         className="sp-plain-dropdown "
//                         value={selectedState}
//                         options={stateOptions}
//                         onChange={(e) => handleStateChange(e.value)}
//                         placeholder="All States"
//                         optionLabel="name"
//                         filter
//                         scrollHeight="160px"
//                         virtualScrollerOptions={{ itemSize: 40 }}
//                       />
//                     </div>

//                     <div className="sp-field sp-field-outline">
//                       <span className="sp-field-legend">City</span>
//                       <Dropdown
//                         inputId="city"
//                         className="sp-plain-dropdown"
//                         value={selectedCity}
//                         options={cityOptions}
//                         onChange={handleCityChange}
//                         placeholder={
//                           selectedState ? "All Cities" : "Select a state"
//                         }
//                         optionLabel="name"
//                         filter
//                         disabled={!selectedState}
//                         scrollHeight="160px"
//                         virtualScrollerOptions={{ itemSize: 40 }}
//                       />
//                     </div>
//                   </div>

//                   <div className="sp-field sp-field-outline">
//                     <span className="sp-field-legend">Search</span>
//                     <InputText
//                       id="search"
//                       placeholder="Search by name or address"
//                       className="sp-plain-input w-full"
//                       value={searchInput}
//                       onChange={handleSearchInput}
//                     />
//                   </div>

//                   {hasActiveFilters && (
//                     <button
//                       type="button"
//                       className="sp-clear-filters"
//                       onClick={handleClearFilters}
//                     >
//                       Clear all filters
//                     </button>
//                   )}
//                 </div>
//                 <div className="sp-results-count">
//                   {filteredServicePoints.length} service point
//                   {filteredServicePoints.length === 1 ? "" : "s"} found
//                 </div>

//                 <div className="sp-results-list">
//                   {!filteredServicePoints?.length && (
//                     <div className="empty-service-point-list">
//                       No Service Points Found
//                     </div>
//                   )}

//                   {filteredServicePoints?.map((point: any) => (
//                     <div
//                       key={point.id}
//                       className={
//                         "location-sp-option-wrapper" +
//                         (selected?.id === point.id ? " is-selected" : "")
//                       }
//                     >
//                       <div className="location-sp-name-wrapper">
//                         <div className="location-sp-name">{point.name}</div>
//                         <div className="location-sp-address">
//                           {point.address}
//                         </div>
//                         {/* Display distance if reference point exists */}
//                         {getReferencePoint() && point.distance !== Infinity && (
//                           <div className="location-sp-distance">
//                             📍 {formatDistance(point.distance)} away
//                           </div>
//                         )}
//                       </div>
//                       <div className="location-sp-locate-btn">
//                         <Button
//                           type="button"
//                           disabled={!point.location}
//                           onClick={(e) => {
//                             e.preventDefault();
//                             e.stopPropagation();
//                             handleServicePointSelection(point);
//                           }}
//                         >
//                           Select
//                         </Button>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             <button
//               type="button"
//               className="locate-me-btn"
//               onClick={handleLocateMe}
//               disabled={locatingMe}
//               title="Show my current location"
//               aria-label="Show my current location"
//             >
//               {locatingMe ? (
//                 <span className="locate-me-spinner" />
//               ) : (
//                 <svg
//                   width="20"
//                   height="20"
//                   viewBox="0 0 24 24"
//                   fill="none"
//                   xmlns="http://www.w3.org/2000/svg"
//                 >
//                   <circle cx="12" cy="12" r="3" fill="#2563eb" />
//                   <path
//                     d="M12 2V5M12 19V22M2 12H5M19 12H22"
//                     stroke="#2563eb"
//                     strokeWidth="2"
//                     strokeLinecap="round"
//                   />
//                   <circle
//                     cx="12"
//                     cy="12"
//                     r="7"
//                     stroke="#2563eb"
//                     strokeWidth="1.5"
//                   />
//                 </svg>
//               )}
//             </button>

//             {locateError && (
//               <div className="locate-me-error">{locateError}</div>
//             )}

//             <GoogleMap
//               mapContainerStyle={{ width: "100%", height: "94vmin" }}
//               center={mapCenter}
//               zoom={mapZoom}
//             >
//               {filteredServicePoints
//                 ?.filter((p: any) => p.location)
//                 ?.map((point: any) => (
//                   <Marker
//                     key={point.id}
//                     position={point.location}
//                     onClick={() => setSelected(point)}
//                     icon={customMarkerIcon}
//                   />
//                 ))}

//               {currentLocation && (
//                 <Marker
//                   position={currentLocation}
//                   icon={currentLocationIcon}
//                   zIndex={999}
//                 />
//               )}

//               {selected?.location && (
//                 <InfoWindow
//                   position={selected.location}
//                   onCloseClick={() => setSelected(null)}
//                 >
//                   <div className="map-location-tooltip">
//                     <div className="selected-service-point-name">
//                       {selected?.name}
//                     </div>
//                     <div className="selected-service-point-address">
//                       {selected?.address ?? "Address Not Available"}
//                     </div>
//                     {/* Show distance in info window */}
//                     {getReferencePoint() &&
//                       selected.distance !== Infinity && (
//                         <div className="selected-service-point-distance">
//                           Distance: {formatDistance(selected.distance)}
//                         </div>
//                       )}
//                     <div className="selected-service-point-goto-location-wrapper">
//                       <div
//                         className="selected-service-point-goto-location"
//                         onClick={() =>
//                           getDirectionFunc(
//                             selected?.location.lat,
//                             selected?.location.lng,
//                           )
//                         }
//                       >
//                         Go To Location
//                       </div>
//                     </div>
//                   </div>
//                 </InfoWindow>
//               )}
//             </GoogleMap>
//           </div>
//         </div>
//       </LoadScript>
//     </div>
//   );
// };

// export default StoreLocator;

// import {
//   GoogleMap,
//   InfoWindow,
//   LoadScript,
//   Marker,
// } from "@react-google-maps/api";
// import { Button } from "primereact/button";
// import { Dropdown } from "primereact/dropdown";
// import { InputText } from "primereact/inputtext";
// import { useEffect, useMemo, useState } from "react";
// import { useDispatch } from "react-redux";
// import useHeroAPI from "../../api/useHeroAPI";
// import usePreDefinedListAPI from "../../api/usePreDefinedListAPI";
// import { setLoading } from "../../redux/loadingSlice";

// import "./StoreLocator.css";
// import AppSkeleton from "../AppSkeleton/AppSkeleton";

// const libraries: "places"[] = ["places"];

// // NOTE: adjust these two keys if your service point API response uses
// // different field names for the parent state / city ids.
// const SP_STATE_ID_KEY = "state_id";
// const SP_CITY_ID_KEY = "city_id";

// const StoreLocator = () => {
//   // Fallback center used until we know the user's location / selection
//   const defaultCenter = { lat: 19.186021, lng: 72.856318 }; // Mumbai

//   const dispatch = useDispatch();
//   const { getSPList } = usePreDefinedListAPI();
//   const { predefinedFunc } = useHeroAPI();

//   const [stateOptions, setStateOptions] = useState<any[]>([]);
//   const [selectedState, setSelectedState] = useState<any>(null);

//   const [cityOptions, setCityOptions] = useState<any[]>([]);
//   const [selectedCity, setSelectedCity] = useState<any>(null);

//   const [selected, setSelected] = useState<any>(null);
//   const [searchInput, setSearchInput] = useState("");
//   const [allServicePoints, setAllServicePoints] = useState<any[]>([]);

//   const [mapLoaded, setMapLoaded] = useState(false);
//   const [locationChecked, setLocationChecked] = useState(false);
//   const [fetchingServicePoints, setFetchingServicePoints] = useState(false);

//   const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(
//     window.innerWidth >= 760,
//   );
//   const [mapCenter, setMapCenter] = useState(defaultCenter);
//   const [mapZoom, setMapZoom] = useState(5);

//   const [currentLocation, setCurrentLocation] = useState<any>(null);
//   const [locatingMe, setLocatingMe] = useState(false);
//   const [locateError, setLocateError] = useState<string | null>(null);

//   const customMarkerIcon = {
//     url: "https://mechmiles.com/location-pin.png",
//     scaledSize: { width: 50, height: 50 } as google.maps.Size,
//   };

//   const currentLocationIcon = {
//     path: window.google?.maps?.SymbolPath?.CIRCLE,
//     scale: 8,
//     fillColor: "#2563eb",
//     fillOpacity: 1,
//     strokeColor: "#ffffff",
//     strokeWeight: 2,
//   };

//   useEffect(() => {
//     const hasReloaded = sessionStorage.getItem("storeLocatorReloaded");

//     if (!hasReloaded) {
//       sessionStorage.setItem("storeLocatorReloaded", "true");
//       window.location.reload();
//     }

//     return () => {
//       sessionStorage.removeItem("storeLocatorReloaded");
//     };
//   }, []);

//   // ---------- API helpers ----------

//   const fetchStateOptions = async () => {
//     try {
//       const response = await predefinedFunc({ entity_type: "STATE" });
//       const states =
//         response?.data?.data?.map((child: any) => ({
//           name: child.name,
//           code: child.predefined_id,
//         })) || [];
//       setStateOptions(states);
//       return states;
//     } catch (err) {
//       console.error("Failed to fetch states", err);
//       return [];
//     } finally {
//       dispatch(setLoading(false));
//     }
//   };

//   const fetchCityOptions = async (state: any) => {
//     try {
//       const response = await predefinedFunc({
//         entity_type: "CITY",
//         parent_id: state.code,
//       });
//       const cities = response?.data?.data || [];
//       setCityOptions(cities);
//       return cities;
//     } catch (err) {
//       console.error("Failed to fetch cities", err);
//       return [];
//     }
//   };

//   // Fetch every service point once; all state/city/search narrowing happens
//   // client-side afterwards (see `filteredServicePoints` below).
//   const fetchAllServicePoints = async () => {
//     try {
//       setFetchingServicePoints(true);
//       dispatch(setLoading(true));
//       const response: any = await getSPList({});
//       if (response.status === 200) {
//         const mapped = (response?.data || [])
//           .map((sp: any) => ({
//             id: sp?.service_point_id,
//             name: sp?.service_point_name,
//             location:
//               sp?.lat && sp?.lng
//                 ? { lat: parseFloat(sp.lat), lng: parseFloat(sp.lng) }
//                 : null,
//             subTitle: sp?.description,
//             address: sp?.service_point_address,
//             phone: sp?.service_point_contact,
//             stateId: sp?.[SP_STATE_ID_KEY],
//             cityId: sp?.[SP_CITY_ID_KEY],
//           }))
//           .sort((a: any, b: any) => {
//             if (a.location && !b.location) return -1;
//             if (!a.location && b.location) return 1;
//             return 0;
//           });
//         setAllServicePoints(mapped);
//       }
//     } finally {
//       dispatch(setLoading(false));
//       setFetchingServicePoints(false);
//     }
//   };

//   // ---------- Geolocation-driven auto selection ----------

//   const detectUserLocation = (states: any[]) => {
//     if (!navigator.geolocation) {
//       setLocationChecked(true);
//       return;
//     }

//     navigator.geolocation.getCurrentPosition(
//       (position) => {
//         const { latitude, longitude } = position.coords;
//         const geocoder = new window.google.maps.Geocoder();

//         geocoder.geocode(
//           { location: { lat: latitude, lng: longitude } },
//           async (results: any, status: any) => {
//             if (status === "OK" && results?.length) {
//               const addressComponents = results[0].address_components;

//               const stateComponent = addressComponents.find((c: any) =>
//                 c.types.includes("administrative_area_level_1"),
//               );
//               const cityComponent = addressComponents.find((c: any) =>
//                 c.types.includes("locality"),
//               );

//               const matchedState = stateComponent
//                 ? states.find(
//                     (s: any) =>
//                       s.name.trim().toLowerCase() ===
//                       stateComponent.long_name.trim().toLowerCase(),
//                   )
//                 : null;

//               if (matchedState) {
//                 setSelectedState(matchedState);
//                 const cities = await fetchCityOptions(matchedState);

//                 const matchedCity = cityComponent
//                   ? cities.find(
//                       (c: any) =>
//                         c.name.trim().toLowerCase() ===
//                         cityComponent.long_name.trim().toLowerCase(),
//                     )
//                   : null;

//                 if (matchedCity) {
//                   setSelectedCity(matchedCity);
//                   if (matchedCity?.lat && matchedCity?.lng) {
//                     setMapCenter({
//                       lat: parseFloat(matchedCity.lat),
//                       lng: parseFloat(matchedCity.lng),
//                     });
//                     setMapZoom(11);
//                   }
//                 }
//               }
//             }
//             setLocationChecked(true);
//           },
//         );
//       },
//       () => setLocationChecked(true),
//     );
//   };

//   // ---------- Effects ----------

//   // Step 1: fetch state list + the full service point list in parallel on mount
//   useEffect(() => {
//     dispatch(setLoading(true));
//     fetchStateOptions();
//     fetchAllServicePoints();
//   }, []);

//   // Step 2: once map + states are ready, try to auto-detect state/city from geolocation
//   useEffect(() => {
//     if (mapLoaded && stateOptions.length > 0 && !locationChecked) {
//       detectUserLocation(stateOptions);
//     }
//   }, [mapLoaded, stateOptions, locationChecked]);

//   useEffect(() => {
//     const handleResize = () => setIsSearchPanelOpen(window.innerWidth >= 760);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // Recenter the map whenever the state/city selection narrows, so results
//   // stay visible even if geolocation didn't drive the change.
//   useEffect(() => {
//     if (selectedCity?.lat && selectedCity?.lng) {
//       setMapCenter({
//         lat: parseFloat(selectedCity.lat),
//         lng: parseFloat(selectedCity.lng),
//       });
//       setMapZoom(11);
//     } else if (selectedState?.lat && selectedState?.lng) {
//       setMapCenter({
//         lat: parseFloat(selectedState.lat),
//         lng: parseFloat(selectedState.lng),
//       });
//       setMapZoom(7);
//     } else if (!selectedState && !selectedCity) {
//       setMapCenter(defaultCenter);
//       setMapZoom(5);
//     }
//   }, [selectedState, selectedCity]);

//   // ---------- Client-side filtering ----------
//   // No state selected -> all points. State only -> all points in that state.
//   // State + city -> points in that city. Search narrows whatever is above.
//   const filteredServicePoints = useMemo(() => {
//     let points = allServicePoints;

//     if (selectedCity) {
//       points = points.filter(
//         (p: any) => String(p.cityId) === String(selectedCity.predefined_id),
//       );
//     } else if (selectedState) {
//       points = points.filter(
//         (p: any) => String(p.stateId) === String(selectedState.code),
//       );
//     }

//     if (searchInput.trim()) {
//       const term = searchInput.trim().toLowerCase();
//       points = points.filter(
//         (p: any) =>
//           p.name?.toLowerCase().includes(term) ||
//           p.address?.toLowerCase().includes(term),
//       );
//     }

//     return points;
//   }, [allServicePoints, selectedState, selectedCity, searchInput]);

//   // ---------- Handlers ----------

//   const handleStateChange = async (state: any) => {
//     if (!state) {
//       setSelectedState(null);
//       setSelectedCity(null);
//       setCityOptions([]);
//       return;
//     }
//     setSelectedState(state);
//     setSelectedCity(null);
//     await fetchCityOptions(state);
//   };

//   const handleCityChange = (e: any) => {
//     const city = e.value;
//     if (!city) {
//       setSelectedCity(null);
//       setSelected(null);
//       return;
//     }
//     setSelectedCity(city);
//   };

//   const handleClearFilters = () => {
//     setSelectedState(null);
//     setSelectedCity(null);
//     setCityOptions([]);
//     setSearchInput("");
//     setSelected(null);
//   };

//   const handleSearchInput = (e: any) => setSearchInput(e.target.value);

//   const handleServicePointSelection = (servicePoint: any) => {
//     setSelected(servicePoint);
//     if (servicePoint.location) {
//       setMapCenter(servicePoint.location);
//       setMapZoom(15);
//     }
//     if (window.innerWidth < 760) setIsSearchPanelOpen(false);
//   };

//   const handleLocateMe = () => {
//     if (!navigator.geolocation) {
//       setLocateError("Geolocation is not supported by this browser.");
//       return;
//     }
//     setLocatingMe(true);
//     setLocateError(null);
//     navigator.geolocation.getCurrentPosition(
//       (position) => {
//         const { latitude, longitude } = position.coords;
//         setCurrentLocation({ lat: latitude, lng: longitude });
//         setMapCenter({ lat: latitude, lng: longitude });
//         setMapZoom(15);
//         setLocatingMe(false);
//       },
//       () => {
//         setLocateError("Unable to retrieve your location.");
//         setLocatingMe(false);
//       },
//       { enableHighAccuracy: true, timeout: 10000 },
//     );
//   };

//   const getDirectionFunc = (lat: any, lng: any) => {
//     const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
//     const link = document.createElement("a");
//     link.href = url;
//     link.target = "_blank";
//     link.rel = "noopener noreferrer";
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   const hasActiveFilters = Boolean(
//     selectedState || selectedCity || searchInput,
//   );

//   return (
//     <div className="map-wrapper">
//       {fetchingServicePoints && (
//         // <div className="loading-overlay">Fetching Service Points Data...</div>
//         <AppSkeleton
//           count={1}
//           width="100p%"
//           height="100%"
//           borderRadius="12px"
//           containerClassName="popular-brands-skeleton-grid"
//           className="popular-brands-skeleton-item"
//         />
//       )}

//       <LoadScript
//         googleMapsApiKey="AIzaSyBc8f4IQtJeK5lr0iEhUy0KbFQCe4JCPnQ"
//         libraries={libraries}
//         onLoad={() => setMapLoaded(true)}
//       >
//         <div className="container-fluid p-0">
//           <div className="map-container">
//             {!isSearchPanelOpen && window.innerWidth < 760 && (
//               <div
//                 className="reopen-search-panel"
//                 onClick={() => setIsSearchPanelOpen(true)}
//               >
//                 🔍
//               </div>
//             )}

//             {isSearchPanelOpen && mapLoaded && (
//               <div className="location-search-panel11">
//                 <div className="sp-panel-header">
//                   <span className="sp-panel-title">Find a Service Point</span>
//                   {window.innerWidth < 760 && (
//                     <button
//                       type="button"
//                       className="sp-panel-close"
//                       onClick={() => setIsSearchPanelOpen(false)}
//                       aria-label="Close"
//                     >
//                       ✕
//                     </button>
//                   )}
//                 </div>

//                 {/* <div className="sp-filters">
//                   <div className="sp-field">
//                     <label htmlFor="state">State</label>
//                     <Dropdown
//                       inputId="state"
//                       className="w-full"
//                       value={selectedState}
//                       options={stateOptions}
//                       onChange={(e) => handleStateChange(e.value)}
//                       placeholder="All States"
//                       optionLabel="name"
//                       showClear
//                       filter
//                       virtualScrollerOptions={{ itemSize: 30 }}
//                     />
//                   </div>

//                   <div className="sp-field">
//                     <label htmlFor="city">City</label>
//                     <Dropdown
//                       inputId="city"
//                       className="w-full"
//                       value={selectedCity}
//                       options={cityOptions}
//                       onChange={handleCityChange}
//                       placeholder={
//                         selectedState ? "All Cities" : "Select a state first"
//                       }
//                       optionLabel="name"
//                       showClear
//                       filter
//                       disabled={!selectedState}
//                       virtualScrollerOptions={{ itemSize: 30 }}
//                     />
//                   </div>

//                   <div className="sp-field">
//                     <label htmlFor="search">Search</label>
//                     <span className="p-input-icon-left w-full">

//                       <InputText
//                         id="search"
//                         placeholder="Search by name or address"
//                         className="w-full"
//                         value={searchInput}
//                         onChange={handleSearchInput}
//                       />
//                     </span>
//                   </div>

//                   {hasActiveFilters && (
//                     <button
//                       type="button"
//                       className="sp-clear-filters"
//                       onClick={handleClearFilters}
//                     >
//                       Clear all filters
//                     </button>
//                   )}
//                 </div> */}
//                 <div className="sp-filters">
//                   <div className="sp-row-two">
//                     <div className="sp-field sp-field-outline">
//                       <span className="sp-field-legend">State</span>
//                       <Dropdown
//                         inputId="state"
//                         className="sp-plain-dropdown "
//                         value={selectedState}
//                         options={stateOptions}
//                         onChange={(e) => handleStateChange(e.value)}
//                         placeholder="All States"
//                         optionLabel="name"
//                         filter
//                         scrollHeight="160px"
//                         virtualScrollerOptions={{ itemSize: 40 }}
//                       />
//                     </div>

//                     <div className="sp-field sp-field-outline">
//                       <span className="sp-field-legend">City</span>
//                       <Dropdown
//                         inputId="city"
//                         className="sp-plain-dropdown"
//                         value={selectedCity}
//                         options={cityOptions}
//                         onChange={handleCityChange}
//                         placeholder={
//                           selectedState ? "All Cities" : "Select a state"
//                         }
//                         optionLabel="name"
//                         filter
//                         disabled={!selectedState}
//                         scrollHeight="160px"
//                         virtualScrollerOptions={{ itemSize: 40 }}
//                       />
//                     </div>
//                   </div>

//                   <div className="sp-field sp-field-outline">
//                     <span className="sp-field-legend">Search</span>
//                     <InputText
//                       id="search"
//                       placeholder="Search by name or address"
//                       className="sp-plain-input w-full"
//                       value={searchInput}
//                       onChange={handleSearchInput}
//                     />
//                   </div>

//                   {hasActiveFilters && (
//                     <button
//                       type="button"
//                       className="sp-clear-filters"
//                       onClick={handleClearFilters}
//                     >
//                       Clear all filters
//                     </button>
//                   )}
//                 </div>
//                 <div className="sp-results-count">
//                   {filteredServicePoints.length} service point
//                   {filteredServicePoints.length === 1 ? "" : "s"} found
//                 </div>

//                 <div className="sp-results-list">
//                   {!filteredServicePoints?.length && (
//                     <div className="empty-service-point-list">
//                       No Service Points Found
//                     </div>
//                   )}

//                   {filteredServicePoints?.map((point: any) => (
//                     <div
//                       key={point.id}
//                       className={
//                         "location-sp-option-wrapper" +
//                         (selected?.id === point.id ? " is-selected" : "")
//                       }
//                     >
//                       <div className="location-sp-name-wrapper">
//                         <div className="location-sp-name">{point.name}</div>
//                         <div className="location-sp-address">
//                           {point.address}
//                         </div>
//                       </div>
//                       <div className="location-sp-locate-btn">
//                         <Button
//                           type="button"
//                           disabled={!point.location}
//                           onClick={(e) => {
//                             e.preventDefault();
//                             e.stopPropagation();
//                             handleServicePointSelection(point);
//                           }}
//                         >
//                           Select
//                         </Button>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             <button
//               type="button"
//               className="locate-me-btn"
//               onClick={handleLocateMe}
//               disabled={locatingMe}
//               title="Show my current location"
//               aria-label="Show my current location"
//             >
//               {locatingMe ? (
//                 <span className="locate-me-spinner" />
//               ) : (
//                 <svg
//                   width="20"
//                   height="20"
//                   viewBox="0 0 24 24"
//                   fill="none"
//                   xmlns="http://www.w3.org/2000/svg"
//                 >
//                   <circle cx="12" cy="12" r="3" fill="#2563eb" />
//                   <path
//                     d="M12 2V5M12 19V22M2 12H5M19 12H22"
//                     stroke="#2563eb"
//                     strokeWidth="2"
//                     strokeLinecap="round"
//                   />
//                   <circle
//                     cx="12"
//                     cy="12"
//                     r="7"
//                     stroke="#2563eb"
//                     strokeWidth="1.5"
//                   />
//                 </svg>
//               )}
//             </button>

//             {locateError && (
//               <div className="locate-me-error">{locateError}</div>
//             )}

//             <GoogleMap
//               mapContainerStyle={{ width: "100%", height: "94vmin" }}
//               center={mapCenter}
//               zoom={mapZoom}
//             >
//               {filteredServicePoints
//                 ?.filter((p: any) => p.location)
//                 ?.map((point: any) => (
//                   <Marker
//                     key={point.id}
//                     position={point.location}
//                     onClick={() => setSelected(point)}
//                     icon={customMarkerIcon}
//                   />
//                 ))}

//               {currentLocation && (
//                 <Marker
//                   position={currentLocation}
//                   icon={currentLocationIcon}
//                   zIndex={999}
//                 />
//               )}

//               {selected?.location && (
//                 <InfoWindow
//                   position={selected.location}
//                   onCloseClick={() => setSelected(null)}
//                 >
//                   <div className="map-location-tooltip">
//                     <div className="selected-service-point-name">
//                       {selected?.name}
//                     </div>
//                     <div className="selected-service-point-address">
//                       {selected?.address ?? "Address Not Available"}
//                     </div>
//                     <div className="selected-service-point-goto-location-wrapper">
//                       <div
//                         className="selected-service-point-goto-location"
//                         onClick={() =>
//                           getDirectionFunc(
//                             selected?.location.lat,
//                             selected?.location.lng,
//                           )
//                         }
//                       >
//                         Go To Location
//                       </div>
//                     </div>
//                   </div>
//                 </InfoWindow>
//               )}
//             </GoogleMap>
//           </div>
//         </div>
//       </LoadScript>
//     </div>
//   );
// };

// export default StoreLocator;
